import { KeyboardArrowDown, PhoneInTalkOutlined, WarningAmber } from '@mui/icons-material';
import { Alert, Autocomplete, CircularProgress, InputBase, Switch, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { formatPhoneNumber, getPhoneOptionLabel } from '../utils/formatnumber';
import axios from 'axios';
import AudioPlayerForRouting from '../reusables/AudioPlayerForRouting';
import TooltipComponent from '../reusables/TooltipComponent';
import RouteSelect from '../reusables/RouteSelect';
import { useRouteConfigModals } from '../contexts/RouteConfigModalsContext';
import { brand } from '../../theme/brandTokens';

const IncommingPhonenumber = ({route, setRoute, setSnackMsg, setOpenSnack, setLoader, hasValidationError = false}) => {
    const [incomming_phonenumbers, setIncommingPhonenumbers] = useState([])
    const [phoneNumbersLoaded, setPhoneNumbersLoaded] = useState(false)
    const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false)

    // Prepopulate with configured_phone from route when editing (similar to Dialer's primary_phone default)
    useEffect(() => {
      if (route?.configured_phone && !phoneNumbersLoaded && incomming_phonenumbers.length === 0) {
        setIncommingPhonenumbers([
          { phoneNumber: route.configured_phone, friendlyName: formatPhoneNumber(route.configured_phone) || route.configured_phone },
        ]);
      }
    }, [route?.configured_phone, phoneNumbersLoaded, incomming_phonenumbers.length]);
    const [localRecordings] = useState(
      () => JSON.parse(localStorage.getItem("organization"))?.recordings?.L || []
    )
    const routeModals = useRouteConfigModals();
    const recordings = routeModals?.recordings ?? localRecordings;

    const handleCallGreetingRecordingChange = useCallback((value) => {
      setRoute((prev) => ({ ...prev, callGreetingRecording: value || "" }));
    }, [setRoute]);

    const addRecordingOption = routeModals
      ? { label: "Add Recording", onClick: () => routeModals.openAddRecordingModal(handleCallGreetingRecordingChange) }
      : undefined;

    // Get organization settings for inbound calls
    const getOrganizationPolicies = () => {
      const organization = JSON.parse(localStorage.getItem("organization"));
      const recordingConfig = organization?.recording_transcription_config?.M || {};
      return {
        inboundRecordingPolicy: recordingConfig.inbound_recording?.S || "disallow",
        inboundTranscriptionPolicy: recordingConfig.inbound_transcription?.S || "disallow",
      };
    };


  
    
      async function getIncomingPhoneNumbers() {
        if (phoneNumbersLoaded || loadingPhoneNumbers) return;
        setLoadingPhoneNumbers(true);
        const org = JSON.parse(localStorage.getItem("organization"));
        await axios
          .post(
            process.env.REACT_APP_BACKEND_URL + "twilio/get-incoming-phone-numbers",
            { organization_id: org.orgId.S }
          )
          .then((res) => {
            let data = res.data;
            if (res.data.length < 1) {
                setOpenSnack(true)
              setSnackMsg(
                "please add an incoming phone number in your twilio account"
              );
              setLoadingPhoneNumbers(false);
            } else {
              setIncommingPhonenumbers(res.data);
              const first = res.data[0]?.phoneNumber;
              if (first) {
                setRoute((prev) => (prev?.configured_phone ? prev : { ...prev, configured_phone: first }));
              }
              setPhoneNumbersLoaded(true);
              setLoadingPhoneNumbers(false);
            }
          })
          .catch((err) => {
            // Log for production diagnostics when get incoming phone numbers fails
            console.error("IncommingPhonenumber: get incoming phone numbers failed", err.response?.data || err.message || err);
            if (err.response && err.response.data) {
              setOpenSnack(true)
              setSnackMsg(err.response.data.message);
            }
            setLoadingPhoneNumbers(false);
          });


          
      }


  return (
    <div className={`border-2 border-gray-200 rounded-xl p-6 bg-white lg:w-[740px] w-full ${hasValidationError ? "!bg-red-50/80" : ""}`}>
      <div className="flex items-start space-x-3">
        <PhoneInTalkOutlined className="text-blue-500 mt-1" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Incoming Call
          </h2>
          <p className="text-sm text-gray-600">
            Configure which phone number receives calls
          </p>
        </div>
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-800 mb-1">
          Phone Number
        </label>
        <Autocomplete
  value={
    incomming_phonenumbers?.find(
      (n) => n.phoneNumber === route?.configured_phone
    ) || null
  }
  onChange={(e, newValue) => {
    setRoute({
      ...route,
      configured_phone: newValue ? newValue.phoneNumber : "",
    });
  }}
  onOpen={() => {
    if (!phoneNumbersLoaded && !loadingPhoneNumbers) {
      getIncomingPhoneNumbers();
    }
  }}
  loading={loadingPhoneNumbers}
  options={incomming_phonenumbers || []}
  getOptionLabel={(option) =>
    option ? getPhoneOptionLabel(option) : ""
  }
  renderInput={(params) => (
    <InputBase
      {...params.InputProps}
      inputProps={params.inputProps}
      placeholder="Select..."
      sx={{
        bgcolor: "white",
        width: "100%",
        px: 2,
        py: 1,
        minHeight: "40px",
        height: "40px",
        fontSize: "15px",
        border: "1px solid #D1D5DB",
        borderRadius: "8px",
      }}
      endAdornment={
        <>
          {loadingPhoneNumbers ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            params.InputProps.endAdornment
          )}
        </>
      }
    />
  )}
  renderOption={(props, option) => (
    <li {...props}>
      {getPhoneOptionLabel(option)}
    </li>
  )}
  popupIcon={<KeyboardArrowDown />}
  fullWidth
  disableClearable
  ListboxProps={{
    style: { maxHeight: 260 },
  }}
/>

      </div>

      {/* Recording and Transcription Toggles */}
      {(() => {
        const { inboundRecordingPolicy, inboundTranscriptionPolicy } = getOrganizationPolicies();
        
        // Check if route has recording enabled but org policy disallows it (edge case)
        const hasRecordingConflict = inboundRecordingPolicy === "disallow" && route.recordCall === true;
        const hasTranscriptionConflict = (inboundTranscriptionPolicy === "disallow" || inboundRecordingPolicy === "disallow") && route.transcribeCall === true;
        
        // Determine recording state: require -> always on, disallow -> always off, allow -> use route.recordCall
        const recordingChecked = inboundRecordingPolicy === "require" || 
          (inboundRecordingPolicy === "allow" && route.recordCall === true);
        const recordingDisabled = inboundRecordingPolicy === "disallow" || inboundRecordingPolicy === "require";
        
        // Determine transcription state: require -> always on, disallow or recording disabled -> always off, allow -> use route.transcribeCall
        const transcriptionChecked = inboundTranscriptionPolicy === "require" || 
          (inboundTranscriptionPolicy === "allow" && route.transcribeCall === true && recordingChecked);
        const transcriptionDisabled = inboundTranscriptionPolicy === "disallow" || 
          inboundRecordingPolicy === "disallow" || 
          inboundTranscriptionPolicy === "require" ||
          !recordingChecked;
        
        return (
          <div className="mt-4 pt-4 border-t border-gray-200">
            {/* Warning for policy conflicts */}
            {(hasRecordingConflict || hasTranscriptionConflict) && (
              <Alert 
                severity="warning" 
                icon={<WarningAmber fontSize="small" />}
                sx={{ mb: 2, fontSize: '12px', '& .MuiAlert-message': { fontSize: '12px' } }}
              >
                {hasRecordingConflict && hasTranscriptionConflict
                  ? "This route had recording and transcription enabled, but they've been disabled due to your organization's policy."
                  : hasRecordingConflict
                  ? "This route had recording enabled, but it's been disabled due to your organization's policy."
                  : "This route had transcription enabled, but it's been disabled due to your organization's policy."}
              </Alert>
            )}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Record Call</span>
                {recordingDisabled ? (
                  <Tooltip
                    title={
                      inboundRecordingPolicy === "disallow"
                        ? "Call recording is disabled by your organization's policy"
                        : "Call recording is required by your organization's policy"
                    }
                    arrow
                  >
                    <span>
                      <Switch
                        checked={recordingChecked}
                        disabled={true}
                        size="small"
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": {
                            color: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
                            backgroundColor: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-disabled": {
                            color: "#D1D5DB",
                          },
                          "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                            backgroundColor: "#E5E7EB",
                          },
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Switch
                    checked={recordingChecked}
                    onChange={(e) => {
                      setRoute({
                        ...route,
                        recordCall: e.target.checked,
                        transcribeCall: e.target.checked === false ? false : route.transcribeCall,
                      });
                    }}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: brand.primary,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: brand.primary,
                      },
                    }}
                  />
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <span className={`text-sm ${
                  transcriptionDisabled && !transcriptionChecked
                    ? "text-gray-400"
                    : "text-gray-700"
                }`}>
                  Transcribe Call
                </span>
                {transcriptionDisabled ? (
                  <Tooltip
                    title={
                      inboundRecordingPolicy === "disallow"
                        ? "Transcription requires call recording to be enabled"
                        : inboundTranscriptionPolicy === "disallow"
                        ? "Call transcription is disabled by your organization's policy"
                        : inboundTranscriptionPolicy === "require"
                        ? "Call transcription is required by your organization's policy"
                        : !recordingChecked
                        ? "Call recording must be enabled to enable transcription"
                        : "Call transcription is disabled"
                    }
                    arrow
                  >
                    <span>
                      <Switch
                        checked={transcriptionChecked}
                        disabled={true}
                        size="small"
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": {
                            color: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
                            backgroundColor: brand.primary,
                          },
                          "& .MuiSwitch-switchBase.Mui-disabled": {
                            color: "#D1D5DB",
                          },
                          "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                            backgroundColor: "#E5E7EB",
                          },
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Switch
                    checked={transcriptionChecked}
                    onChange={(e) => {
                      setRoute({
                        ...route,
                        transcribeCall: e.target.checked,
                      });
                    }}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: brand.primary,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: brand.primary,
                      },
                    }}
                  />
                )}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Call Greeting</span>
                <Switch
                  checked={route.callGreetingEnabled === true}
                  onChange={(e) => {
                    setRoute({
                      ...route,
                      callGreetingEnabled: e.target.checked,
                      callGreetingRecording: e.target.checked ? (route.callGreetingRecording || "") : "",
                    });
                  }}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: brand.primary,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: brand.primary,
                    },
                  }}
                />
              </div>
            </div>

            {route.callGreetingEnabled && (
              <div className="mt-3">
                <div className="flex items-center gap-1 mb-1">
                  <label className="text-sm font-medium text-gray-800">
                    Call Greeting
                  </label>
                  <TooltipComponent message="A message that plays right away—helpful for welcoming callers or disclosing recording before proceeding to your call actions." />
                </div>
                <RouteSelect
                  value={route.callGreetingRecording || ""}
                  onChange={(value) => {
                    setRoute({
                      ...route,
                      callGreetingRecording: value || "",
                    });
                  }}
                  options={recordings?.map((item) => ({ value: item.M.rec_id.S, label: item.M.title.S })) ?? []}
                  placeholder="Select a recording"
                  addOption={addRecordingOption}
                />
                {route.callGreetingRecording && (() => {
                  const greetingUrl = recordings?.find((e) => e.M?.rec_id?.S === route.callGreetingRecording)?.M?.url?.S;
                  return greetingUrl ? <AudioPlayerForRouting src={greetingUrl} /> : null;
                })()}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  )
}

export default IncommingPhonenumber